import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { apiClient } from "@/config/api";
import {
  STATEMENTS_API_ENDPOINT,
  STATEMENTS_ERROR_MESSAGES,
} from "@/utils/constants";
import {
  GetStatementsParams,
  GetStatementsResponse,
  SyncStatementsResponse,
} from "./types";
import { SyncStartResult, SyncStatus } from "@/types/sync";

/**
 * Builds query string from statement filter parameters
 * @param params - The filter parameters for statements
 * @returns URL with query string for the statements API
 */
const buildStatementsUrl = (params: GetStatementsParams): string => {
  const queryParams = new URLSearchParams();

  if (params.page) {
    queryParams.append("page", params.page.toString());
  }
  if (params.propertyId) {
    queryParams.append("propertyId", params.propertyId);
  }
  if (params.year) {
    queryParams.append("year", params.year.toString());
  }
  if (params.month) {
    queryParams.append("month", params.month.toString());
  }

  const queryString = queryParams.toString();
  return queryString
    ? `${STATEMENTS_API_ENDPOINT}?${queryString}`
    : STATEMENTS_API_ENDPOINT;
};

/**
 * Fetches paginated statements with optional filters
 * @param params - Filter parameters including page, propertyId, year, month
 * @returns Promise resolving to GetStatementsResponse
 */
export const getStatements = createAsyncThunk(
  "statements/getStatements",
  async (params: GetStatementsParams, { rejectWithValue }) => {
    try {
      const url = buildStatementsUrl(params);
      const response = await apiClient.get<{ data: GetStatementsResponse }>(url);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Include status code for timeout detection
        return rejectWithValue({
          message:
            error.response?.data?.error?.message ||
            STATEMENTS_ERROR_MESSAGES.GET_STATEMENTS_FAILED,
          status: error.response?.status,
        });
      }
      return rejectWithValue({
        message: STATEMENTS_ERROR_MESSAGES.GET_STATEMENTS_FAILED,
      });
    }
  }
);

/**
 * Fetches additional statements for pagination (load more)
 * @param params - Filter parameters including page, propertyId, year, month
 * @returns Promise resolving to GetStatementsResponse
 */
export const getMoreStatements = createAsyncThunk(
  "statements/getMoreStatements",
  async (params: GetStatementsParams, { rejectWithValue }) => {
    try {
      const url = buildStatementsUrl(params);
      const response = await apiClient.get<{ data: GetStatementsResponse }>(url);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Include status code for timeout detection
        return rejectWithValue({
          message:
            error.response?.data?.error?.message ||
            STATEMENTS_ERROR_MESSAGES.GET_MORE_STATEMENTS_FAILED,
          status: error.response?.status,
        });
      }
      return rejectWithValue({
        message: STATEMENTS_ERROR_MESSAGES.GET_MORE_STATEMENTS_FAILED,
      });
    }
  }
);

/**
 * Syncs month-end statements from Streamline for all organizations.
 * Only accessible by MAINSAIL_ADMIN users.
 * @returns Promise resolving to SyncStatementsResponse
 */
export const syncAllStatements = createAsyncThunk(
  "statements/syncAllStatements",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<{ data: SyncStatementsResponse }>(
        `${STATEMENTS_API_ENDPOINT}/sync`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            STATEMENTS_ERROR_MESSAGES.SYNC_ALL_FAILED
        );
      }
      return rejectWithValue(STATEMENTS_ERROR_MESSAGES.SYNC_ALL_FAILED);
    }
  }
);

/**
 * Starts a month-end statements sync for the caller's organization.
 * POST /statements/sync/:organizationId — pass the user's own org id.
 * 202 -> started; 409 -> a sync is already running for the org (returned as a
 * "conflict" result, not an error, so callers can show the wait message).
 * @param organizationId - The caller's organization ID
 */
export const syncOrganizationStatements = createAsyncThunk(
  "statements/syncOrganizationStatements",
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<{
        data: { startedAt?: string | null; retryAfterMinutes?: number };
      }>(`${STATEMENTS_API_ENDPOINT}/sync/${organizationId}`);
      const data = response.data.data ?? {};
      const result: SyncStartResult = {
        status: "started",
        startedAt: data.startedAt ?? null,
        retryAfterMinutes: data.retryAfterMinutes,
      };
      return result;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const data = error.response.data?.data ?? {};
        const conflict: SyncStartResult = {
          status: "conflict",
          startedAt: data.startedAt ?? null,
          retryAfterMinutes: data.retryAfterMinutes,
          message:
            data.message ||
            "A statements sync is already running for your organization.",
        };
        return conflict;
      }
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            STATEMENTS_ERROR_MESSAGES.SYNC_ORGANIZATION_FAILED
        );
      }
      return rejectWithValue(STATEMENTS_ERROR_MESSAGES.SYNC_ORGANIZATION_FAILED);
    }
  }
);

/**
 * Reads the org-wide statements-sync lock status.
 * GET /statements/sync/status
 */
export const getStatementsSyncStatus = createAsyncThunk(
  "statements/getSyncStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<{ data: SyncStatus }>(
        `${STATEMENTS_API_ENDPOINT}/sync/status`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Failed to fetch statements sync status."
        );
      }
      return rejectWithValue("Failed to fetch statements sync status.");
    }
  }
);
